import Button from "@mui/material/Button";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  GROUP_SEPARATOR_SX,
  dailyHeadSx,
  estimateHeadSx,
  headCellSx,
  pinnedHeadSx,
} from "./closeTableStyles";
import {
  COLUMN_GROUP_KEYS,
  COLUMN_GROUP_TITLE,
  ColumnGroupKey,
  EXTRA_COLUMN_TITLE,
  GROUP_TOTAL_TITLE,
} from "./constants";
import { CloseColumn, CloseLayout, columnKeyOf, isGroupStart } from "./monthlyExpenseLayout";

const HEADER_CORNER_LAYER = 4;

interface GroupToggleProps {
  groupKey: ColumnGroupKey;
  isExpanded: boolean;
  onToggle: (groupKey: ColumnGroupKey) => void;
}

function GroupToggle({ groupKey, isExpanded, onToggle }: Readonly<GroupToggleProps>) {
  const handleClick = () => onToggle(groupKey);
  return (
    <Button
      size="small"
      color="inherit"
      onClick={handleClick}
      aria-expanded={isExpanded}
      startIcon={isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
      sx={{ fontWeight: 600 }}
    >
      {COLUMN_GROUP_TITLE[groupKey]}
    </Button>
  );
}

interface CloseTableHeadProps {
  layout: CloseLayout;
  columns: CloseColumn[];
  expanded: Record<ColumnGroupKey, boolean>;
  /** "ก.ย. 2026" — the month the divisor and the actual figures are of. */
  monthTitle: string;
  /** "ประมาณการ ต.ค. 2026". */
  estimateTitle: string;
  onToggleGroup: (groupKey: ColumnGroupKey) => void;
}

const labelOf = (column: CloseColumn) => {
  if (column.kind === "EXTRA") return EXTRA_COLUMN_TITLE;
  if (column.kind === "GROUP_TOTAL") return GROUP_TOTAL_TITLE;
  return column.label;
};

/**
 * Two sticky rows: the groups (each folds to its รวม) and the estimate group, then the column
 * names. The hovers say which figure is used where (handbook "ยอดไหนถูกใช้ที่ไหน").
 */
export default function CloseTableHead({
  layout,
  columns,
  expanded,
  monthTitle,
  estimateTitle,
  onToggleGroup,
}: Readonly<CloseTableHeadProps>) {
  const extraHint = `ค่าใช้จ่ายที่เกิดขึ้นจริงแต่ไม่อยู่ใน 16 รายการ · นับทั้งยอดจริงของเดือน${monthTitle}และ${estimateTitle}`;
  const estimateHint = `ยอดหลังปรับ (ตัวเอียง) ใช้เป็น${estimateTitle} ในหน้าประมาณการเดือนนี้ของสาขาและรายงานโหมดประมาณการ · ยอดที่ระบบคำนวณ = ยอดจริงของเดือน${monthTitle} ใช้ในรายงานโหมดจริง ไม่เปลี่ยนตามการปัด/แก้ทับ`;

  return (
    <TableHead>
      <TableRow>
        <TableCell rowSpan={2} sx={[headCellSx(1), pinnedHeadSx("BRANCH", HEADER_CORNER_LAYER), { textAlign: "left" }]}>
          สาขา
        </TableCell>
        <TableCell rowSpan={2} align="center" sx={[headCellSx(1), pinnedHeadSx("DAYS", HEADER_CORNER_LAYER)]}>
          <Tooltip title={`นับจากการวิ่งจริงของเดือน${monthTitle}`}>
            <span>
              วันขาย
              <Typography variant="caption" display="block" color="text.secondary">
                จริง
              </Typography>
            </span>
          </Tooltip>
        </TableCell>
        <TableCell rowSpan={2} align="center" sx={[headCellSx(1), pinnedHeadSx("ROUTES", HEADER_CORNER_LAYER)]}>
          <Tooltip title={`สายหลักที่ไม่ใช่ test ที่วิ่งจริงในเดือน${monthTitle}`}>
            <span>
              สาย
              <Typography variant="caption" display="block" color="text.secondary">
                จริง
              </Typography>
            </span>
          </Tooltip>
        </TableCell>
        {COLUMN_GROUP_KEYS.map((groupKey) => (
          <TableCell
            key={groupKey}
            colSpan={expanded[groupKey] ? layout[groupKey].length + 1 : 1}
            align="center"
            sx={[headCellSx(1), GROUP_SEPARATOR_SX, groupKey === "DAILY" ? dailyHeadSx : {}]}
          >
            <GroupToggle groupKey={groupKey} isExpanded={expanded[groupKey]} onToggle={onToggleGroup} />
          </TableCell>
        ))}
        <TableCell colSpan={2} align="center" sx={[headCellSx(1), GROUP_SEPARATOR_SX, estimateHeadSx]}>
          <Tooltip title={estimateHint}>
            <span>{estimateTitle} · ต่อสายต่อวัน</span>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        {columns.map((column, index) => (
          <TableCell
            key={columnKeyOf(column)}
            title={labelOf(column)}
            sx={[
              headCellSx(2),
              isGroupStart(columns, index) ? GROUP_SEPARATOR_SX : {},
              column.kind === "EXTRA" ? { color: "primary.dark" } : {},
            ]}
          >
            {column.kind === "EXTRA" ? (
              <Tooltip title={extraHint}>
                <span>{EXTRA_COLUMN_TITLE}</span>
              </Tooltip>
            ) : (
              labelOf(column)
            )}
          </TableCell>
        ))}
        <TableCell sx={[headCellSx(2), GROUP_SEPARATOR_SX, estimateHeadSx]}>Sales</TableCell>
        <TableCell sx={[headCellSx(2), estimateHeadSx]}>Manager</TableCell>
      </TableRow>
    </TableHead>
  );
}
